export const articles = [
  {
    slug: "how-i-use-opencode-with-deepseek",
    title: "How I use OpenCode with DeepSeek",
    excerpt:
      "What DeepSeek is, why I connected its API to OpenCode, and the simple loop I use for small personal projects.",
    date: "2026-10-18",
    readingTime: "3 min read",
    tags: ["OpenCode", "DeepSeek", "Notes"],
    body: [
      {
        type: "p",
        text: "I use OpenCode with DeepSeek. I am not a professional developer; I just like tinkering with my personal website. This is a plain explanation of the setup, not a tutorial.",
      },
      { type: "h2", text: "What is DeepSeek?" },
      {
        type: "p",
        text: "DeepSeek is an AI company from China that builds large language models. It has a chat app, but it also has an API that other tools can call. You can think of the API as a way for a program to ask the model a question and get an answer back.",
      },
      {
        type: "p",
        text: "The API is compatible with the OpenAI and Anthropic formats, and DeepSeek's documentation lists OpenCode as one of the tools that can use it. I use a DeepSeek API key in my setup, not the consumer chat app. You can find DeepSeek at deepseek.com.",
      },
      { type: "h2", text: "Why I connected the API to OpenCode" },
      {
        type: "p",
        text: "OpenCode is a coding agent that runs in the terminal. It can read files, edit them, and run commands. But it still needs a model to make decisions. The DeepSeek API is that model for me. OpenCode sends the request; DeepSeek sends a suggestion; I decide whether to keep it.",
      },
      {
        type: "p",
        text: "Keeping them separate is nice. I can change the model later without changing how I work. I can also see the diff and run the build myself.",
      },
      { type: "h2", text: "My simple loop" },
      {
        type: "ul",
        items: [
          "I describe one small task and point to the files that matter.",
          "I ask it to read first and explain what it found.",
          "I let it change one thing, then I read the diff.",
          "I run the build and tests. If something breaks, I paste the error back.",
          "I keep secrets, accounts, and deployments away from the tool.",
        ],
      },
      { type: "h2", text: "What I use it for" },
      {
        type: "p",
        text: "Small edits to this site, explaining an error I do not understand, writing a first draft, and doing repetitive chores. It is helpful, but it is not magic. I still have to check the result.",
      },
      { type: "h2", text: "What to watch out for" },
      {
        type: "p",
        text: "DeepSeek, like any model, can be confidently wrong. It may invent a command, misread a file, or solve a slightly different problem. The API also means my prompts leave my computer and go to DeepSeek's servers, so I never paste passwords, API keys, or private data.",
      },
      { type: "h2", text: "The short version" },
      {
        type: "p",
        text: "DeepSeek is the model. OpenCode is the tool that talks to it. I use the DeepSeek API because OpenCode needs an endpoint, not a chat window. It helps me make small changes, and I stay responsible for the result.",
      },
      {
        type: "quote",
        text: "A useful assistant is not one that knows everything. It is one that helps you check your own work.",
      },
    ],
  },
  {
    slug: "why-i-love-opencode",
    title: "Why I love OpenCode",
    excerpt:
      "A coding agent that lives in the terminal, shows its work, and does not lock me into one model or one editor.",
    date: "2026-10-16",
    readingTime: "3 min read",
    tags: ["OpenCode", "Tools"],
    body: [
      {
        type: "p",
        text: "I spend a lot of my free time in a terminal. I am not a professional developer; I just like tinkering with my personal website. Most coding tools ask me to leave the terminal, open a panel, and trust a black box. OpenCode does the opposite. It is an open-source coding agent that runs where the work already is, and that is most of why I love it.",
      },
      { type: "h2", text: "It lives where the work already is" },
      {
        type: "p",
        text: "OpenCode reads the repository, edits files, runs commands, and uses tools through the Model Context Protocol. It can plan a change, show the diff, and let me run the tests. Nothing is hidden behind a proprietary editor or a browser tab that wants to become my whole workflow.",
      },
      {
        type: "p",
        text: "That matters more than it sounds. When a tool lives in the terminal, it composes with everything else I already use: git, npm, grep, a test runner, a deployment script. I do not have to move my work into the tool. The tool comes to the work.",
      },
      { type: "h2", text: "Bring your own model" },
      {
        type: "p",
        text: "The agent and the model are separate decisions. OpenCode can work with different providers, so I can choose a model for the task and change it when cost, speed, or quality changes. I am not buying an editor and a model in the same box.",
      },
      {
        type: "p",
        text: "This is not a sponsored post. I rebuilt this website with OpenCode, and I kept using it because the experience was calm. It read the existing code, followed the patterns that were already there, and made changes I could review. I am still a beginner, so a tool that explains itself is worth more to me than one that acts clever.",
      },
      { type: "h2", text: "It shows its work" },
      {
        type: "p",
        text: "A coding agent should be legible. I want to see which files changed, what command ran, and what the result was. When something goes wrong, I want a trail, not a apology. OpenCode keeps the loop visible enough that I can stop it, correct it, or take over.",
      },
      {
        type: "ul",
        items: [
          "Small fixes on my personal site: change one thing, then check the result.",
          "Explaining code I do not understand, without pretending I wrote it.",
          "Boring chores: updating a sitemap, adjusting a layout, or finding a typo.",
        ],
      },
      {
        type: "p",
        text: "I do not use it for unreviewed production deploys, secrets, or decisions that need a human. No tool should make those feel automatic. The value is in the small, reversible work that makes the rest of the day easier.",
      },
      { type: "h2", text: "The tool should be boring" },
      {
        type: "p",
        text: "The best tools disappear into the work. OpenCode is not trying to become a personality or a platform I have to live inside. It is a terminal command, a set of tools, and a model I can swap. That is exactly the kind of boring I want from my development environment.",
      },
    ],
  },
  {
    slug: "why-i-still-keep-a-personal-website",
    title: "Why I still keep a personal website",
    excerpt:
      "Social profiles come and go. A small site of your own is slower, quieter, and far more useful than it looks.",
    date: "2026-10-12",
    readingTime: "3 min read",
    tags: ["Web", "Notes"],
    body: [
      {
        type: "p",
        text: "Almost everyone I know has a profile somewhere. A feed, a timeline, a page that someone else designed. It is convenient, and it is also borrowed land. The rules can change, the reach can disappear, and the design is never really yours.",
      },
      {
        type: "p",
        text: "A personal website is different. It is small, it is quiet, and nobody is going to algorithmically bury it because I posted something that did not get enough reactions. That is most of the reason I keep this one.",
      },
      { type: "h2", text: "A plot of the internet that is mine" },
      {
        type: "p",
        text: "I bought aimanre.my because I wanted a place to point people to. Not a portfolio in the polished, agency sense, but a home for the things I make and think about. When I meet someone new, I can send one link instead of hoping they scroll far enough down a feed.",
      },
      {
        type: "p",
        text: "The site also forces me to decide what I actually want to say. A feed rewards the quick reaction. A website rewards the considered one. The difference is small until you try it, and then it is not small at all.",
      },
      { type: "h2", text: "Writing makes me think better" },
      {
        type: "p",
        text: "I do not write because I have everything figured out. I write to find out what I think. Putting an idea into sentences exposes the gaps: the assumptions I borrowed, the parts I only half understand, the advice I have never actually followed.",
      },
      {
        type: "quote",
        text: "A website is less like a stage and more like a workbench. You can leave something half-finished on it and come back tomorrow.",
      },
      {
        type: "p",
        text: "That is why the writing here is not a news feed. There is no publishing schedule and no hot take. When something feels worth keeping, it gets a page.",
      },
      { type: "h2", text: "Small, slow, and useful" },
      {
        type: "p",
        text: "A personal site can be built in an afternoon. It does not need a database, a login system, or a design system with forty components. Mine is a React app, a handful of pages, and a folder of text. That is enough.",
      },
      {
        type: "ul",
        items: [
          "Start with one page that says who you are and what you care about.",
          "Write three pieces you would want a stranger to read.",
          "Make it fast, readable, and easy to update. Everything else is optional.",
        ],
      },
      {
        type: "p",
        text: "The internet is full of places that want your attention. A personal website is one of the few places built to give it back. If you have been thinking about making one, I can only share what worked for me: buy the domain, put up a single honest page, and see how it feels.",
      },
    ],
  },
  {
    slug: "buying-your-first-domain-in-malaysia",
    title: "What I learned buying my first domain in Malaysia",
    excerpt:
      "Notes from my own experience choosing a name, understanding .my domains, renewals, DNS, and what I did on day one.",
    date: "2026-09-28",
    readingTime: "3 min read",
    tags: ["Notes", "Web"],
    body: [
      {
        type: "p",
        text: "A domain is the one part of a website you can own for years without touching the code. It is also the part I rushed when I bought my first one. These are my notes from that experience, not expert advice. I am still learning.",
      },
      { type: "h2", text: "Pick a name you can say out loud" },
      {
        type: "p",
        text: "The best test is simple: can you say it to someone at a coffee shop and have them type it correctly on the first try? Short beats clever. Avoid numbers that can be written as words, hyphens that get forgotten, and spellings you have to repeat twice.",
      },
      {
        type: "ul",
        items: [
          "Use your name if the site is personal. It is the easiest thing to remember.",
          "Keep it short enough to fit on a business card and in a social bio.",
          "Check that the same handle is free on the platforms you actually use.",
        ],
      },
      { type: "h2", text: "Where does a .my domain come from?" },
      {
        type: "p",
        text: "Malaysia's country-code domain is .my, administered by MYNIC. You do not register directly with MYNIC. You go through an accredited registrar, the same way you buy a .com through a registrar. Compare the total cost, not just the first-year promotion.",
      },
      {
        type: "p",
        text: "For a .com or .net, any reputable international registrar works. Cloudflare Registrar sells at wholesale cost and does not run aggressive renewal upsells, though it requires you to manage DNS inside Cloudflare. Local registrars are often easier to pay with a Malaysian card and can help with local support.",
      },
      { type: "h2", text: "Read the renewal price" },
      {
        type: "p",
        text: "A RM10 first year can become RM80 at renewal. That is not a scam, but it is easy to miss. Look at the renewal price, the transfer policy, and whether privacy protection is included. Set a calendar reminder before the renewal date so a lapsed card does not take the site down.",
      },
      {
        type: "p",
        text: "Also decide whether you need email at the domain. A hello@yourdomain.com address looks more trustworthy than a free inbox, and many registrars bundle forwarding. Forwarding is enough to start; you do not need a full mailbox on day one.",
      },
      { type: "h2", text: "Point the domain at your site" },
      {
        type: "p",
        text: "Once the domain is yours, you connect it to a host by changing nameservers or DNS records. Nameservers hand the whole DNS job to your host. Individual records let you keep DNS where it is and point only the website. For a first site, letting the host manage DNS is usually the least confusing option.",
      },
      {
        type: "p",
        text: "After the change, propagation can take anywhere from a few minutes to a day. Do not panic-refresh. Use a DNS checker to confirm the records, then wait. Turn on HTTPS through your host or a free certificate service so the padlock appears.",
      },
      { type: "h2", text: "A short day-one checklist" },
      {
        type: "ul",
        items: [
          "Enable auto-renew and two-factor authentication on the registrar account.",
          "Turn on WHOIS privacy if it is not already included.",
          "Point the domain at your host and enable HTTPS.",
          "Set up email forwarding for hello@ and admin@.",
          "Write down where the domain is registered and who has access.",
        ],
      },
      {
        type: "p",
        text: "None of this is glamorous, which is exactly why it is worth doing properly. A domain is a long-term address. Treat it like one, and the rest of the website gets a stable place to live.",
      },
    ],
  },
  {
    slug: "the-quiet-beauty-of-betta-fish",
    title: "The quiet beauty of Betta fish",
    excerpt:
      "A small tank, a stubborn little fish, and the surprisingly useful habit of paying attention.",
    date: "2026-09-10",
    readingTime: "3 min read",
    tags: ["Life", "Hobbies"],
    body: [
      {
        type: "p",
        text: "Betta fish, also known as Siamese fighting fish, are one of the most recognisable freshwater fish in the world. They come in colours that look almost painted on, with fins that move like fabric in slow water. It is easy to buy one because it is pretty. It is better to buy one because you are ready to care for a small, specific life.",
      },
      { type: "h2", text: "More than a pretty fish" },
      {
        type: "p",
        text: "The betta's reputation is a little unfair. They are territorial, especially males, but they are not aggressive for no reason. Put two males together and they will fight. Give one a properly sized tank with places to hide and it will spend most of its time exploring, resting, and watching you back.",
      },
      {
        type: "p",
        text: "That last part surprised me. A fish is not a decoration that happens to move. It has preferences. It notices when the light changes. It learns where food appears. Keeping one well is less about having a beautiful object and more about maintaining a tiny environment.",
      },
      { type: "h2", text: "A tank is a small ecosystem" },
      {
        type: "p",
        text: "The common mistake is treating the tank as a bowl of water. Water is a chemistry problem. Waste becomes ammonia, ammonia becomes nitrite, and both are harmful. A filter and a colony of beneficial bacteria do the quiet work of keeping the water safe. The fish cannot do it alone, and neither can a weekly water change.",
      },
      {
        type: "ul",
        items: [
          "Give the fish space. A small tank is harder to keep stable, not easier.",
          "Keep the temperature steady. Bettas are tropical fish and do not enjoy cold water.",
          "Feed a varied diet, in small amounts, and remove what is not eaten.",
          "Change part of the water regularly and treat new water before adding it.",
          "Cycle the tank before the fish arrives. Patience is part of the equipment.",
        ],
      },
      {
        type: "p",
        text: "None of this requires a laboratory. It requires a test kit, a heater, a filter, and the willingness to learn what the numbers mean. The first time the water stays clear and the fish stops hiding, the effort feels worth it.",
      },
      { type: "h2", text: "The lesson in the glass" },
      {
        type: "p",
        text: "A betta tank is a good reminder that care is mostly repetition. Feeding, checking, changing a little water, noticing a change in behaviour. There is no dramatic moment when everything is finished. The reward is a quiet, healthy system that keeps working because someone paid attention.",
      },
      {
        type: "quote",
        text: "A small life in a glass box makes the ordinary act of paying attention feel worthwhile.",
      },
      {
        type: "p",
        text: "I would not recommend a fish to someone who wants a low-effort ornament. I would recommend one to someone who wants a small, daily practice of noticing. The colours are a bonus.",
      },
    ],
  },
  {
    slug: "small-habits-for-a-clearer-desk",
    title: "Five small habits that keep my desk (and head) clear",
    excerpt:
      "Not a productivity system. Just a few small rules that make it easier to sit down and start.",
    date: "2026-08-22",
    readingTime: "2 min read",
    tags: ["Life", "Notes"],
    body: [
      {
        type: "p",
        text: "I have tried the elaborate systems. Colour-coded calendars, apps with nested projects, a notebook for each area of life. They all worked for about two weeks. What has lasted is much smaller: a few habits that reduce the friction between deciding to work and actually working.",
      },
      { type: "h2", text: "One surface, cleared at the end" },
      {
        type: "p",
        text: "At the end of the day, I clear the top of my desk. Not the drawers, not the cables behind the monitor, just the surface I look at. It takes two minutes. The next morning starts with a place to put the laptop instead of a small archaeology project.",
      },
      { type: "h2", text: "The next action goes on paper" },
      {
        type: "p",
        text: "Before I stop, I write the next concrete action on a sticky note. Not a project name, not a goal. Something like 'reply to the client email' or 'fix the mobile menu'. When I sit down again, I do not have to reconstruct where I was. The note does the remembering.",
      },
      { type: "h2", text: "Notifications stay off by default" },
      {
        type: "p",
        text: "My phone is on silent, and the laptop only shows badges for messages that need a reply that day. The world does not end. Most notifications are other people's urgency arriving in my attention. Checking them in batches is a small boundary that makes deep work possible.",
      },
      { type: "h2", text: "One tab at a time" },
      {
        type: "p",
        text: "I used to keep thirty tabs open as a kind of visual to-do list. Now I keep the current task and one reference. If I need something later, it goes into a note or a bookmark instead of becoming another strip at the top of the screen. The browser feels lighter, and so do I.",
      },
      { type: "h2", text: "Stop while it is still working" },
      {
        type: "p",
        text: "This one is borrowed from writers: end the day in the middle of something you understand. Leave a small thread to pull tomorrow. It feels wrong to stop early, but starting is much easier when the first step is already obvious.",
      },
      {
        type: "p",
        text: "None of these habits will make anyone twice as productive. They just make the desk a little quieter and the start of the day a little easier. That is usually enough.",
      },
    ],
  },
];

export function getArticle(slug) {
  return articles.find((article) => article.slug === slug);
}

export function getRelatedArticles(slug, limit = 2) {
  return articles.filter((article) => article.slug !== slug).slice(0, limit);
}

export function formatDate(date) {
  return new Date(`${date}T00:00:00`).toLocaleDateString("en-MY", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
